import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Zap, Cog, BarChart3, Shield } from 'lucide-react';
import styles from './Login.module.css';
import Logo from '../Images/hyphenwhite.png';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  login,
  loginmessage,
  requestOTP,
  verifyOTP,
  resendOTP,
  setUserName
} from "../../actions/auth";
import { fetchLoginLogo } from "../../actions/logowhitelisting";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeService, setActiveService] = useState(0);
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const canvasRef = useRef(null);

  const apiData = useSelector((state) => state?.auth);
  const loginmess = apiData?.authData?.status_code === 200 ? null : apiData?.authData;
  const { login_logo } = useSelector(
    (state) => state?.logowhitelisting?.images || {}
  );

  const services = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Single Window Pane",
      description: "A unified view that simplifies monitoring, reduces cognitive load, and enables quicker, data-driven decisions.",
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Analytics Dashboard",
      description: "Real-time insights and predictive analytics to make data-driven decisions with confidence.",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Unified Data Integration",
      description: "Ready-to-use connectors for databases and APIs ensure smooth data ingestion.",
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: "Operational & Service Insights",
      description: "Detects issues early and monitors SLIs to keep operations smooth.",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    const response = await dispatch(login({
      username: email,
      password: password,
      database_type: "postgres"
    }, navigate));

    if (response === false) {
      setLoginAttempted(true);
      if (loginmess && loginmess?.message.includes("not registered")) {
        toast.error("Username is not registered.");
      } else {
        toast.error("Incorrect email or password. Please try again.");
        dispatch(setUserName({ username: email }));
      }
    } else if (response === true) {
      toast.success("Logged In successfully");
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }
      setTimeout(() => {
        navigate('/Dashboard');
      }, 1000);
    }
  };

  const validateForgotPassword = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/.+@.+\..+/.test(email)) newErrors.email = 'Invalid email format';
    else if (email.length > 45) newErrors.email = 'Email must be 45 characters or less';
    return newErrors;
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    const validationErrors = validateForgotPassword();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const success = await dispatch(requestOTP({
      email: email,
      database_type: "postgres"
    }));

    if (success) {
      toast.success("OTP sent! Please check your email.");
      setOtpSent(true);
      setResendTimer(60);
      setIsTimerRunning(true);
    } else {
      toast.error("Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error('Please enter the OTP.');
      return;
    }

    const success = await dispatch(verifyOTP({
      email: email,
      otp: otp,
      database_type: "postgres"
    }));

    if (success) {
      toast.success('OTP verified! Redirecting to password reset...');
      setTimeout(() => {
        navigate("/resetNew");
      }, 1000);
    } else {
      toast.error('Invalid OTP. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    if (isTimerRunning) {
      return;
    }

    const success = await dispatch(resendOTP({
      email: email,
      database_type: "postgres"
    }));

    if (success) {
      toast.success('OTP resent successfully!');
      setResendTimer(60);
      setIsTimerRunning(true);
      setOtp('');
    } else {
      toast.error('Failed to resend OTP');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmNewPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    toast.info('Please complete password reset on the next page');
  };

  useEffect(() => {
    dispatch(loginmessage());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLoginLogo(1));
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % services.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [services.length]);

  useEffect(() => {
    if (resendTimer > 0 && isTimerRunning) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && isTimerRunning) {
      setIsTimerRunning(false);
    }
  }, [resendTimer, isTimerRunning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let particles = [];
    const particleCount = 50;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 5 + 1;
        this.speedX = Math.random() * 2 - 1;
        this.speedY = Math.random() * 2 - 1;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
        this.size -= 0.05;
        if (this.size < 0) {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.size = Math.random() * 5 + 1;
        }
      }

      draw() {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });
      requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.background}>
          <div className={styles.backgroundImage} />
          <canvas ref={canvasRef} className={styles.particleCanvas}></canvas>
          <div className={styles.gradientOverlay} />
        </div>

        <div className={styles.mainContainer}>
          <div className={styles.grid}>
            <div className={styles.leftSide}>
              <div className={styles.loginForm}>
                <div className={styles.formHeader}>
                  <div className={styles.logo}>
                    {login_logo ? (
                      <img src={login_logo} alt="Logo" className={styles.logoImg} />
                    ) : (
                      <img src={Logo} alt="Logo" className={styles.logoImg} />
                    )}
                  </div>
                </div>

                <div className={styles.formContent} key={showForgotPassword ? 'forgot' : 'login'}>
                  {showForgotPassword ? (
                    <>
                      <div className={styles.formInputs}>
                        <div className={styles.inputGroup}>
                          <div className={styles.inputWrapper}>
                            <Mail className={styles.inputIcon} />
                            <input
                              type="email"
                              value={email}
                              pattern="^([a-zA-Z0-9_\\-.]+)@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.)|(([a-zA-Z0-9\\-]+\\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\\]?)$"
                              onChange={(e) => setEmail(e.target.value)}
                              className={styles["form-input"]}
                              placeholder="Enter your registered email"
                              readOnly={otpSent}
                              required
                              maxLength={45}
                            />
                          </div>

                          {/* MOVE "SEND OTP" BUTTON BELOW INPUT */}
                          {!otpSent && (
                            <button
                              onClick={handleRequestOtp}
                              className={styles.sendOtpButton}
                            >
                              Send OTP
                            </button>
                          )}

                          {errors.email && (
                            <div className={styles.errorMessage}>{errors.email}</div>
                          )}
                        </div>


                        {otpSent && (
                          <>
                            <div className={styles.inputGroup}>
                              <div className={styles.inputWrapper}>
                                <Lock className={styles.inputIcon} />
                                <input
                                  type="password"
                                  value={otp}
                                  onChange={(e) => setOtp(e.target.value)}
                                  className={styles['form-input']}
                                  placeholder="Enter OTP"
                                  maxLength={6}
                                />
                              </div>
                            </div>

                            <button onClick={handleVerifyOtp} className={styles['submit-button']}>
                              Verify OTP
                            </button>

                            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                              {isTimerRunning ? (
                                <p style={{ color: '#e2e8f0', fontSize: '0.875rem' }}>
                                  Please wait {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, '0')} to resend.
                                </p>
                              ) : (
                                <p style={{ color: '#e2e8f0', fontSize: '0.875rem' }}>
                                  Don't receive the OTP?{' '}
                                  <span
                                    onClick={handleResendOtp}
                                    style={{
                                      color: '#3b82f6',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      fontWeight: '500'
                                    }}
                                  >
                                    RESEND OTP
                                  </span>
                                </p>
                              )}
                            </div>
                          </>
                        )}

                        <div className={styles.backToLoginWrapper}>
                          <button
                            onClick={() => {
                              setShowForgotPassword(false);
                              setEmail('');
                              setPassword('');
                              setConfirmPassword('');
                              setErrors({});
                              setOtpSent(false);
                              setOtp('');
                              setResendTimer(0);
                              setIsTimerRunning(false);
                            }}
                            className={styles.backToLogin}
                          >
                            Back to Login
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={styles.formInputs}>
                      <div className={styles.inputGroup}>
                        <div className={styles.inputWrapper}>
                          <Mail className={styles.inputIcon} />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={styles['form-input']}
                            pattern="^([a-zA-Z0-9_\\-.]+)@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.)|(([a-zA-Z0-9\\-]+\\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\\]?)$"
                            placeholder="Enter your email"
                            maxLength={45}
                            required
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className={styles.inputGroup}>
                        <div className={styles.inputWrapper}>
                          <Lock className={styles.inputIcon} />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={styles['form-input']}
                            placeholder="Enter your password"
                            autoComplete="off"
                            maxLength={15}
                            required
                          />
                          <button
                            onClick={() => setShowPassword(!showPassword)}
                            className={styles['toggle-password']}
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className={styles.rememberMe}>
                        {loginAttempted && (
                          <button
                            onClick={() => {
                              setShowForgotPassword(true);
                              setPassword('');
                              setConfirmPassword('');
                              setErrors({});
                            }}
                            className={styles.forgotPassword}
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <button onClick={handleSubmit} className={styles['submit-button']}>
                        Sign In
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.rightSide}>
              <div className={styles.serviceCards}>
                <div className={styles.cardContainer}>
                  {services.map((service, index) => (
                    <div
                      key={index}
                      className={`${styles['service-card']} ${activeService === index ? styles.active : ''}`}
                      onClick={() => setActiveService(index)}
                    >
                      <div className={styles.cardContent}>
                        <div className={`${styles['card-icon']} ${activeService === index ? styles.active : ''}`}>
                          {service.icon}
                        </div>
                        <div className={styles.cardDetails}>
                          <h3 className={styles['card-title']}>{service.title}</h3>
                          <p className={styles['card-description']}>{service.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;